import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Download, Printer } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getImportBookingDetails } from '@/redux/ImportBooking/actions';
import { getMasterCompanyDetails } from '@/redux/Common/actions';
import { baseURL } from '@/config/APIConfig';
import { axiosInstance } from '@/config/NetworkUtility';

/** Empty = skip proxy. Default wsrv helps PDF when logo host has no CORS. Disable: VITE_DISABLE_ISF_LOGO_PROXY=true */
const ISF_LOGO_PROXY_PREFIX =
    import.meta.env.VITE_DISABLE_ISF_LOGO_PROXY === 'true'
        ? ''
        : (import.meta.env.VITE_EXTERNAL_IMAGE_PROXY_PREFIX ?? 'https://wsrv.nl/?url=');

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const pick = (obj, keys) => {
    for (const key of keys) {
        const value = obj?.[key];
        if (value != null && String(value).trim() !== '') return value;
    }
    return '';
};

const disp = (v) => (v == null || String(v).trim() === '' ? '' : String(v).trim());

const formatDate = (d) =>
    d && moment(d).isValid() ? moment(d).format('DD MMM YYYY').toUpperCase() : '';

const cleanPort = (v) => (v ? String(v).split('[')[0].trim() : '');

const decodeRouteBookingId = (id) => {
    if (id == null || id === '') return null;
    try { return atob(id); } catch { return id; }
};

const resolvePublicAssetUrl = (src) => {
    if (!src || typeof src !== 'string') return '';
    const t = src.trim();
    if (/^https?:\/\//i.test(t) || /^data:/i.test(t)) return t;
    if (/^\/\//.test(t)) return `https:${t}`;
    if (typeof window === 'undefined') return t;
    return `${window.location.origin}${t.startsWith('/') ? '' : '/'}${t}`;
};

/** Cached master row from API/localStorage (DashboardLayout also loads this; refresh here for direct opens). */
const readStoredCompanyDetails = () => {
    try {
        const raw = localStorage.getItem('MasterCompanyDetails');
        if (!raw) return [];
        const p = JSON.parse(raw);
        return Array.isArray(p) ? p : [];
    } catch {
        return [];
    }
};

const pickCompanyLogo = (row) => {
    if (!row || typeof row !== 'object') return '';
    return pick(row, [
        'CompanyLogoDark',
        'companyLogoDark',
        'CompanyLogo',
        'companyLogo',
        'CompanyLogoLight',
        'companyLogoLight',
    ]);
};

/** Blob GET with cookies — works when fetch follows redirects oddly */
const xhrBlob = (url) =>
    new Promise((resolve, reject) => {
        try {
            const x = new XMLHttpRequest();
            x.open('GET', url, true);
            x.responseType = 'blob';
            x.withCredentials = true;
            x.onload = () => {
                if (x.status >= 200 && x.status < 300) resolve(x.response);
                else reject(new Error(String(x.status)));
            };
            x.onerror = () => reject(new Error('xhr'));
            x.send();
        } catch (e) {
            reject(e);
        }
    });

const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onloadend = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });

/** Same as BookingPrint — paths resolve against API base; Bearer helps authenticated file URLs */
const resolveImageRequestUrl = (src) => {
    if (!src || src.startsWith('data:')) return src;
    const t = String(src).trim();
    if (/^https?:\/\//i.test(t)) return t;
    if (t.startsWith('//')) return `${window.location.protocol}${t}`;
    if (t.startsWith('/')) {
        try {
            return new URL(t, window.location.href).href;
        } catch {
            /* fall through */
        }
    }
    const base = baseURL.live.replace(/\/?$/, '/');
    const path = t.startsWith('/') ? t.slice(1) : t;
    return `${base}${path}`;
};

const tryDrawLiveImageToDataUrl = (liveImg) => {
    if (!liveImg || !liveImg.complete || !(liveImg.naturalWidth > 0)) return '';
    try {
        const c = document.createElement('canvas');
        c.width = liveImg.naturalWidth;
        c.height = liveImg.naturalHeight;
        c.getContext('2d').drawImage(liveImg, 0, 0);
        return c.toDataURL('image/png');
    } catch {
        return '';
    }
};

/** Axios blob + fetch (BookingPrint) — works for api.shipez.ai file URLs and some same-site assets */
const fetchImageAsDataUrlViaApi = async (src) => {
    if (!src || src.startsWith('data:')) return src || '';
    const requestUrl = resolveImageRequestUrl(src);
    const token = localStorage.getItem('authToken');
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    try {
        const { data } = await axiosInstance({
            method: 'get',
            url: requestUrl,
            responseType: 'blob',
            headers: { ...authHeaders },
        });
        if (data && typeof data.size === 'number' && data.size === 0) return '';
        if (data) return await blobToDataUrl(data);
    } catch {
        /* continue */
    }

    try {
        const res = await fetch(requestUrl, {
            mode: 'cors',
            credentials: 'include',
            headers: { ...authHeaders },
            cache: 'force-cache',
        });
        if (!res.ok) return '';
        return await blobToDataUrl(await res.blob());
    } catch {
        return '';
    }
};

const fetchLogoViaProxy = async (absoluteHttpUrl) => {
    if (!ISF_LOGO_PROXY_PREFIX || !absoluteHttpUrl || !/^https?:\/\//i.test(absoluteHttpUrl)) {
        return '';
    }
    try {
        const proxied = `${ISF_LOGO_PROXY_PREFIX}${encodeURIComponent(absoluteHttpUrl)}`;
        const r = await fetch(proxied, { mode: 'cors' });
        if (!r.ok) return '';
        const blob = await r.blob();
        if (!blob?.size) return '';
        return await blobToDataUrl(blob);
    } catch {
        return '';
    }
};

const inlineImageUrlViaCanvas = (url) =>
    new Promise((resolve) => {
        const loadViaImage = (useCors) => {
            const img = new Image();
            if (useCors) img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const c = document.createElement('canvas');
                    c.width = img.naturalWidth || 1;
                    c.height = img.naturalHeight || 1;
                    c.getContext('2d').drawImage(img, 0, 0);
                    resolve(c.toDataURL('image/png'));
                } catch {
                    if (!useCors) loadViaImage(true);
                    else resolve('');
                }
            };
            img.onerror = () => {
                if (!useCors) loadViaImage(true);
                else resolve('');
            };
            img.src = url;
        };
        loadViaImage(false);
    });

/**
 * Data URL for PDF / cache — API fetch first, then browser, then optional proxy for other-domain CDN (no CORS).
 */
const logoUrlToDataUrl = async (url, liveImgEl) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;

    const fromLive = tryDrawLiveImageToDataUrl(liveImgEl);
    if (fromLive) return fromLive;

    const fromApi = await fetchImageAsDataUrlViaApi(url);
    if (fromApi && String(fromApi).startsWith('data:')) return fromApi;

    try {
        const r = await fetch(url, { credentials: 'include', mode: 'cors' });
        if (r.ok) return await blobToDataUrl(await r.blob());
    } catch {
        /* continue */
    }

    try {
        return await blobToDataUrl(await xhrBlob(url));
    } catch {
        /* continue */
    }

    const fromImg = await inlineImageUrlViaCanvas(url);
    if (fromImg) return fromImg;

    const fromProxy = await fetchLogoViaProxy(url);
    return fromProxy || '';
};

const waitForDocumentImages = (doc, timeoutMs = 800) => {
    const imgs = [...doc.querySelectorAll('img')];
    if (!imgs.length) return Promise.resolve();
    return Promise.race([
        Promise.all(
            imgs.map(
                (img) =>
                    new Promise((res) => {
                        if (img.complete && img.naturalHeight > 0) {
                            res();
                            return;
                        }
                        img.addEventListener('load', () => res(), { once: true });
                        img.addEventListener('error', () => res(), { once: true });
                    })
            )
        ),
        new Promise((res) => setTimeout(res, timeoutMs)),
    ]);
};

const joinUnique = (data, keys) => {
    const rows = data?.ContainersCargoModel || data?.BOLDescriptionsData || [];
    if (!Array.isArray(rows)) return '';
    const set = new Set();
    rows.forEach((item) => { const v = pick(item, keys); if (v) set.add(String(v).trim()); });
    return [...set].join(', ');
};

const fmt = (name, address) => {
    const n = disp(name), a = disp(address);
    if (!n && !a) return '';
    if (!a) return n;
    if (!n) return a;
    return `${n}\n${a}`;
};

/* ─── Row component ────────────────────────────────────────────────────────── */
const Row = ({ label, children, tall }) => (
    <tr className={tall ? 'isf-row-tall' : ''}>
        <td className="isf-lbl">{label}</td>
        <td className="isf-val">{children}</td>
    </tr>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const ISFPrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const printRef = useRef();
    const { MasterCompanyDetails } = useSelector((s) => s.common);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');

    const { bookingId: bookingIdFromParam } = useParams();
    const { bookingId: bookingIdFromState } = location.state || {};
    const bookingIdFromQuery = new URLSearchParams(location.search).get('bookingId');
    const bookingId =
        bookingIdFromState ||
        decodeRouteBookingId(bookingIdFromParam) ||
        bookingIdFromQuery ||
        null;

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [logoDataUrl, setLogoDataUrl] = useState('');

    const companyRow = useMemo(
        () => MasterCompanyDetails?.[0] || readStoredCompanyDetails()[0] || null,
        [MasterCompanyDetails]
    );

    const companyAddressLines = useMemo(() => {
        const raw = disp(
            pick(companyRow || {}, [
                'ProfileAddressPrint',
                'profileAddressPrint',
                'ProfileAddress',
                'profileAddress',
                'CompanyAddress',
                'companyAddress',
            ])
        );
        if (!raw) return [];
        return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }, [companyRow]);

    const rawCompanyLogo = useMemo(() => pickCompanyLogo(companyRow), [companyRow]);
    const resolvedLogoUrl = useMemo(() => resolvePublicAssetUrl(rawCompanyLogo), [rawCompanyLogo]);
    const displayLogoSrc = logoDataUrl || resolvedLogoUrl;

    /* Same pattern as BookingImportPrint / DeliveryOrderPrint */
    useEffect(() => {
        if (authUser?.companyId) dispatch(getMasterCompanyDetails(authUser.companyId));
    }, [dispatch, authUser?.companyId]);

    useEffect(() => {
        let cancelled = false;
        if (!resolvedLogoUrl) {
            setLogoDataUrl('');
            return;
        }
        logoUrlToDataUrl(resolvedLogoUrl, null).then((d) => {
            if (!cancelled) setLogoDataUrl(d || '');
        });
        return () => { cancelled = true; };
    }, [resolvedLogoUrl]);

    /* load booking data */
    useEffect(() => {
        const load = async () => {
            if (!bookingId || String(bookingId) === '0') { setLoading(false); return; }
            try {
                const result = await dispatch(
                    getImportBookingDetails(bookingId, authUser?.companyId)
                ).catch(() => null);
                setFormData({ ...(result || {}) });
            } finally { setLoading(false); }
        };
        load();
    }, [bookingId, authUser?.companyId, dispatch]);

    /*
     * Fast print: only ISF scoped CSS (no Tailwind linkTags — those slow iframe load heavily).
     * outerHTML keeps .isf-a4 wrapper; innerHTML dropped it before.
     */
    const handlePrint = () => {
        const root = printRef.current;
        if (!root) return;

        const isfCss = document.querySelector('style[data-isf-print]')?.innerHTML ?? '';
        const printMarkup = root.outerHTML;
        const logoSrcForPrint = logoDataUrl || resolvedLogoUrl;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        const iframeDoc = win.document;

        let didPrint = false;
        const cleanup = () => {
            if (iframe.parentNode) document.body.removeChild(iframe);
        };

        iframeDoc.open();
        iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
        <meta charset="UTF-8" />
        <title>ISF - ${pick(formData, ['FileRefNumber', 'HBLNumber']) || 'Document'}</title>
        <base href="${window.location.origin}/" />
        <style>
            @page { size: A4 portrait; margin: 12mm; }
            html, body {
            margin: 0;
            padding: 0;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            }
            ${isfCss}
        </style>
        </head>
        <body>${printMarkup}</body>
        </html>`);
        iframeDoc.close();

        const triggerPrint = () => {
            if (didPrint || !iframe.parentNode) return;
            didPrint = true;
            win.onafterprint = cleanup;
            window.setTimeout(() => {
                try {
                    win.focus();
                    win.print();
                } catch {
                    cleanup();
                }
            }, 50);
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const doc = iframe.contentDocument;
                const logoEl = doc?.querySelector('img.isf-doc-logo');
                if (logoEl && logoSrcForPrint) logoEl.src = logoSrcForPrint;
                waitForDocumentImages(doc, 900).then(triggerPrint);
            });
        });
    };

    const handleDownload = async () => {
        if (!printRef.current || !formData) return;
        const btn = document.querySelector('[data-isf-download-btn]');
        const origText = btn?.textContent;
        try {
            if (btn) { btn.textContent = 'Generating PDF…'; btn.disabled = true; }

            printRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
            window.scrollTo(0, 0);

            const logoImg = printRef.current.querySelector('img.isf-doc-logo');
            let inlineLogo =
                logoDataUrl ||
                (resolvedLogoUrl ? await logoUrlToDataUrl(resolvedLogoUrl, logoImg) : '');
            let prevSrc = null;
            if (logoImg && inlineLogo) {
                prevSrc = logoImg.getAttribute('src');
                logoImg.src = inlineLogo;
                logoImg.removeAttribute('crossorigin');
                try {
                    await logoImg.decode();
                } catch {
                    await new Promise((r) => {
                        if (logoImg.complete && logoImg.naturalHeight > 0) {
                            r();
                            return;
                        }
                        logoImg.onload = r;
                        logoImg.onerror = r;
                        window.setTimeout(r, 4000);
                    });
                }
            }

            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                logging: false,
                backgroundColor: '#ffffff',
                imageTimeout: 12000,
                removeContainer: true,
                onclone: (_clonedDoc, clonedRoot) => {
                    const img = clonedRoot.querySelector('img.isf-doc-logo');
                    if (img && inlineLogo) {
                        img.src = inlineLogo;
                        img.removeAttribute('crossorigin');
                    }
                },
            });

            if (logoImg && prevSrc !== null) logoImg.src = prevSrc;

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pw = pdf.internal.pageSize.getWidth();
            const ph = pdf.internal.pageSize.getHeight();
            const ratio = canvas.width / canvas.height;
            let w = pw - 20, h = w / ratio;
            if (h > ph - 20) { h = ph - 20; w = h * ratio; }
            pdf.addImage(imgData, 'PNG', (pw - w) / 2, (ph - h) / 2, w, h);
            pdf.save(
                `ISF_${pick(formData, ['FileRefNumber', 'HBLNumber']) || 'Document'}_${moment().format('YYYY-MM-DD')}.pdf`
            );
        } catch (err) {
            console.error('PDF error', err);
            alert('Failed to generate PDF. Please try printing instead.');
        } finally {
            if (btn) { btn.textContent = origText ?? 'Download PDF'; btn.disabled = false; }
        }
    };

    /* ── derived display values ─────────────────────────────────────────── */
    const shipperName = disp(pick(formData, ['Shipper']));
    const shipperAddress = disp(pick(formData, ['ShipperAddress']));
    const consigneeName = disp(pick(formData, ['Consignee']));
    const consigneeAddress = disp(pick(formData, ['ConsigneeAddress']));

    const buyerName = formData?.IsBuyer_SameConsign ? consigneeName : disp(pick(formData, ['BuyerName']));
    const buyerAddress = formData?.IsBuyer_SameConsign ? consigneeAddress : disp(pick(formData, ['BuyerAddress']));
    const sellerName = formData?.IsSeller_SameShip ? shipperName : disp(pick(formData, ['SellerName']));
    const sellerAddr = formData?.IsSeller_SameShip ? shipperAddress : disp(pick(formData, ['SellerAddress']));
    const shipToName = formData?.IsShipTo_SameConsign ? consigneeName : disp(pick(formData, ['ShipToName']));
    const shipToAddr = formData?.IsShipTo_SameConsign ? consigneeAddress : disp(pick(formData, ['ShipToAddress']));
    const consoName = formData?.IsConsolidator_SameShip ? shipperName : disp(pick(formData, ['ConsolidatorName']));
    const consoAddr = formData?.IsConsolidator_SameShip ? shipperAddress : disp(pick(formData, ['ConsolidatorAddress']));
    const stufferName = formData?.IsStuffer_SameShip ? shipperName : disp(pick(formData, ['StufferName']));
    const stufferAddr = formData?.IsStuffer_SameShip ? shipperAddress : disp(pick(formData, ['StufferAddress']));

    const consigneeNumbers = [
        disp(pick(formData, ['EntryNo'])),
        disp(pick(formData, ['CCNNo'])),
        disp(pick(formData, ['ITNumber', 'ITNNumber'])),
    ].filter(Boolean).join(', ');

    const tariffHts = disp(joinUnique(formData, ['SCHEDULE_B', 'ScheduleB']));
    const containerNos = disp(joinUnique(formData, ['CONTAINER_NUMBER', 'ContainerNumber']));
    const hblNo = disp(pick(formData, ['HBLNumber', 'HouseBLNo', 'RealHBLNumber', 'SubBLNumber']));
    const oceanCarrier = disp(pick(formData, ['CarrierName']));
    const mblNo = disp(pick(formData, ['CarrierMBLNo', 'ActualBLNo']));
    const amsScac = disp(pick(formData, ['AMS_SCACCode', 'MBL_SCACCode', 'CarrierSCAC']));
    const amsHblNo = disp(pick(formData, ['AMSHBLNo', 'HBLNumber']));
    const feederVessel = disp([
        pick(formData, ['TranshipmentVessel']),
        pick(formData, ['TranshipmentVoyage']),
        pick(formData, ['PreCarrageBy', 'PreCarriageBy']),
    ].filter(Boolean).join(' '));
    const motherVessel = disp(pick(formData, ['VesselName', 'Vessel', 'ConMotherVessel']));
    const portOfLoading = disp(cleanPort(pick(formData, ['PortOfLoading', 'PortOfLoad', 'PortOfLoading_Customized'])));
    const etd = formatDate(pick(formData, ['ETSPOLDate', 'EtdAtPol', 'ETSOrigin']));
    const portOfDischarge = disp(cleanPort(pick(formData, ['PortOfDischarge', 'PortOfDestination', 'PortOfDischarge_Customized'])));
    const podEta = formatDate(pick(formData, ['ETAPOD', 'ArrivalNoticeETA', 'VesselArrivalDate']));
    const finalPlace = disp(pick(formData, ['PlaceOfDelivery', 'FinalDestination', 'ShipezPlaceOfDelivery', 'CarrierPlaceOfDelivery', 'CarrierPlaceOfDelivery_Customized']));
    const sellersInvNo = disp(pick(formData, ['VendorInvoiceNumber', 'InvoiceNumber', 'CustomerPoNo', 'ForwarderRefNo']));
    const countryOfOrigin = disp(pick(formData, ['CountryOfOrigin']));

    /* ── loading / error guards ── */
    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="text-center">
                <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading ISF data…</p>
            </div>
        </div>
    );

    if (!formData) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="text-center max-w-md px-4">
                <p className="text-gray-800 font-medium">Could not load import booking</p>
                <p className="text-gray-600 text-sm mt-2">No data available for this booking ID.</p>
                <button type="button" onClick={() => navigate(-1)}
                    className="mt-4 px-4 py-2 bg-[#1a2744] text-white rounded-lg text-sm">
                    Go back
                </button>
            </div>
        </div>
    );

    /* ── main render ── */
    return (
        <div className="min-h-screen bg-gray-200 print:bg-white">

            {/* action toolbar */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center gap-3">
                    <button type="button" onClick={handleDownload} data-isf-download-btn
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium border border-gray-300">
                        <Download className="w-4 h-4" /> Download PDF
                    </button>
                    <button type="button" onClick={handlePrint}
                        className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg font-medium">
                        <Printer className="w-4 h-4" /> Print
                    </button>
                </div>
            </div>

            {/* A4 document — same logo pattern as importHBLPrint (resolved company asset URL) */}
            <div className="flex justify-center py-6 print:py-0">
                <div ref={printRef} className="isf-a4 bg-white text-black shadow-xl print:shadow-none print:p-0">
                    {/* Must NOT use <header>: global index.css hides all header {} in @media print */}
                    <div
                        className={[
                            'isf-doc-head',
                            displayLogoSrc ? 'isf-doc-head--logo' : 'isf-doc-head--nologo',
                            companyAddressLines.length ? 'isf-doc-head--with-addr' : '',
                        ].filter(Boolean).join(' ')}
                        role="banner"
                    >
                        {displayLogoSrc ? (
                            <div className="isf-doc-logo-cell">
                                <img
                                    src={displayLogoSrc}
                                    alt={companyRow?.CompanyName || companyRow?.companyName || 'Company Logo'}
                                    className="isf-doc-logo"
                                    loading="eager"
                                    decoding="sync"
                                />
                            </div>
                        ) : null}
                        <h1 className="isf-doc-title">ISF DETAILS</h1>
                        {companyAddressLines.length > 0 ? (
                            <div className="isf-doc-addr-cell">
                                {companyAddressLines.map((line, i) => (
                                    <div key={i} className="isf-doc-addr-line">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <table className="isf-details-table">
                        <tbody>
                            <Row label="MANUFACTURER OR SUPPLIER NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(shipperName, shipperAddress)}</span>
                            </Row>
                            <Row label="SELLER NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(sellerName, sellerAddr)}</span>
                            </Row>
                            <Row label="BUYER NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(buyerName, buyerAddress)}</span>
                            </Row>
                            <Row label="SHIP TO NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(shipToName, shipToAddr)}</span>
                            </Row>
                            <Row label="CONTAINER STUFFING LOCATION NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(stufferName, stufferAddr)}</span>
                            </Row>
                            <Row label="CONSOLIDATOR (STUFFER) NAME & ADDRESS:" tall>
                                <span className="isf-multiline">{fmt(consoName, consoAddr)}</span>
                            </Row>
                            <Row label="CONSIGNEE NUMBER(S):">{consigneeNumbers}</Row>
                            <Row label="COUNTRY OF ORIGIN:">{countryOfOrigin}</Row>
                            <Row label="TARIFF / HTS NUMBER:">{tariffHts}</Row>
                            <Row label="SELLER'S INV NO.:">{sellersInvNo}</Row>
                            <Row label="CONTAINER NO.:">{containerNos}</Row>
                            <Row label="HBL NO.:">{hblNo}</Row>
                            <Row label="OCEAN CARRIER:">{oceanCarrier}</Row>
                            <Row label="MBL NO.:">{mblNo}</Row>
                            <Row label="AMS HBL ISSUER SCAC CODE:">{amsScac}</Row>
                            <Row label="AMS HBL NO.:">{amsHblNo}</Row>
                            <Row label="FEEDER VESSEL DETAILS:">{feederVessel}</Row>
                            <Row label="MOTHER VESSEL DETAILS:">{motherVessel}</Row>
                            <Row label="PORT OF LOADING:">{portOfLoading}</Row>
                            <Row label="E.T.D.:">{etd}</Row>
                            <Row label="PORT OF DISCHARGE:">{portOfDischarge}</Row>
                            <Row label="POD ETA:">{podEta}</Row>
                            <Row label="FINAL PLACE OF DELIVERY:">{finalPlace}</Row>
                            <Row label={'\u00a0'}><span className="isf-empty">&nbsp;</span></Row>
                            <Row label={'\u00a0'}><span className="isf-empty">&nbsp;</span></Row>
                        </tbody>
                    </table>
                </div>
            </div>

            <style data-isf-print>{`
                .isf-a4 {
                    width: 210mm; min-height: 297mm; max-width: 100%;
                    box-sizing: border-box; padding: 12px 14px;
                    font-family: Arial, Helvetica, sans-serif;
                    font-size: 11px; color: #000; background: #fff;
                }
                .isf-a4, .isf-a4 * { box-sizing: border-box; }

                /* Grid layout: html2canvas captures flow layout more reliably than absolute */
                .isf-doc-head {
                    display: grid;
                    align-items: center;
                    gap: 10px 16px;
                    padding: 4px 0 14px;
                    min-height: 52px;
                    overflow: visible;
                }
                .isf-doc-head--logo { grid-template-columns: auto 1fr; }
                .isf-doc-head--logo.isf-doc-head--with-addr {
                    grid-template-columns: auto minmax(0, 1fr) minmax(100px, 200px);
                }
                .isf-doc-head--nologo { grid-template-columns: 1fr; }
                .isf-doc-head--nologo.isf-doc-head--with-addr {
                    grid-template-columns: minmax(0, 1fr) minmax(100px, 200px);
                }
                .isf-doc-logo-cell { min-width: 0; }
                .isf-doc-addr-cell {
                    justify-self: end;
                    align-self: center;
                    max-width: 200px;
                    text-align: right;
                    font-size: 9px;
                    line-height: 1.35;
                    font-weight: 600;
                    text-transform: uppercase;
                    color: #000;
                    word-wrap: break-word;
                }
                .isf-doc-addr-line + .isf-doc-addr-line { margin-top: 2px; }
                .isf-doc-logo {
                    height: 48px;
                    width: auto;
                    max-width: 220px;
                    object-fit: contain;
                    display: block !important;
                    visibility: visible !important;
                    print-color-adjust: exact !important;
                    -webkit-print-color-adjust: exact !important;
                }
                .isf-doc-title {
                    margin: 0;
                    padding: 8px 0 10px;
                    text-align: center;
                    font-size: 15px;
                    font-weight: 700;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    text-decoration: underline;
                    text-underline-offset: 3px;
                    color: #000;
                }
                .isf-details-table {
                    width: 100%; border-collapse: collapse;
                    border-spacing: 0; table-layout: fixed; border: 1px solid #000;
                }
                .isf-details-table td {
                    border: 1px solid #000; padding: 5px 8px;
                    vertical-align: top; color: #000; word-wrap: break-word;
                }
                .isf-lbl {
                    width: 40%; font-weight: 700;
                    text-transform: uppercase; font-size: 10px; line-height: 1.35;
                }
                .isf-val {
                    width: 60%; font-weight: 400;
                    text-transform: uppercase; font-size: 10px; line-height: 1.35;
                }
                .isf-row-tall .isf-val { min-height: 52px; }
                .isf-multiline { white-space: pre-line; display: block; }
                .isf-empty     { display: block; min-height: 24px; }

                @media screen {
                    .isf-a4 { margin: 0 auto; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
                }
                @media print {
                    @page { size: A4 portrait; margin: 10mm; }
                    .isf-a4 {
                        width: 100%; min-height: auto;
                        padding: 0 !important; box-shadow: none !important;
                    }
                    .isf-details-table  { page-break-inside: auto; }
                    .print\\:hidden     { display: none !important; }
                    .print\\:py-0       { padding-top: 0 !important; padding-bottom: 0 !important; }
                    .print\\:shadow-none{ box-shadow: none !important; }
                    .print\\:bg-white   { background: white !important; }
                    .print\\:p-0        { padding: 0 !important; }
                }
            `}</style>
        </div>
    );
};

export default ISFPrint;